import { Image, VStack, Text, Tag } from '@chakra-ui/react';
import { tenantsFallbackIcon } from 'features/common/utils/icons';
import { PortalIcon } from 'features/common/components/PortalIcon';
import TruncatedText from 'features/common/components/TruncatedText';

interface IAppTileProps {
  appName: string;
  tenantName: string;
  appLogo?: string;
  onClick: () => void;
  color?: string;
  'data-testid'?: string;
  image?: string;
  ['data-behavior-analytics-id']?: string;
  customerEnvironmentType?: string;
  sitecorePlanId?: string;
  trialExpirationDate?: string;
}

export const AppTile = ({
  appName,
  tenantName,
  appLogo,
  onClick,
  color = 'neutral',
  'data-testid': dataTestId,
  image,
  'data-behavior-analytics-id': dataBehaviorAnalyticsId,
  customerEnvironmentType,
  sitecorePlanId,
}: IAppTileProps) => {
  const hasConnectTrial =
    sitecorePlanId === 'BASIC_TRIAL' || sitecorePlanId === 'EXTENDED_TRIAL';

  return (
    <VStack
      as='button'
      type='button'
      w='full'
      p='4'
      border='0'
      bg='transparent'
      sx={{ font: 'inherit', lineHeight: 'inherit' }}
      onClick={onClick}
      cursor='pointer'
      _hover={{ backgroundColor: 'neutral.100' }}
      _active={{ backgroundColor: 'neutral.200' }}
      _focusVisible={{
        outline: '2px solid',
        outlineColor: 'primary.500',
        outlineOffset: '2px',
      }}
      data-behavior-analytics-id={dataBehaviorAnalyticsId}
      data-testid={dataTestId}
      data-appname={tenantName}
      borderRadius='md'
      transitionProperty='common'
      transitionDuration='normal'
    >
      {image ? (
        <Image boxSize='10' src={image} />
      ) : (
        <PortalIcon
          data-testid='app-tile-default-icon'
          boxSize='10'
          path={appLogo || tenantsFallbackIcon}
          color={`${color}.600`}
        />
      )}
      <VStack spacing='0'>
        <Text
          fontWeight='medium'
          textAlign='center'
          w='full'
          noOfLines={1}
          wordBreak='break-all'
        >
          {appName}
        </Text>
        <TruncatedText
          text={tenantName}
          maxLength={20}
          color='blackAlpha.500'
        />
      </VStack>
      {customerEnvironmentType && (
        <Tag
          colorScheme='neutral'
          size='sm'
          data-testid='app-tile-environment-tag'
        >
          {customerEnvironmentType.toLowerCase()}
        </Tag>
      )}
      {hasConnectTrial && (
        <Tag colorScheme='primary' size='sm' data-testid='app-tile-trial-tag'>
          trial
        </Tag>
      )}
    </VStack>
  );
};
